import { useRef, useState, type ChangeEvent, type KeyboardEvent } from "react";
import {
  Box,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import { AttachFile, Close, Send } from "@mui/icons-material";

export type ChatComposerProps = {
  disabled: boolean;
  onSend: (text: string, image: File | null) => void;
};

export const ChatComposer = ({ disabled, onSend }: ChatComposerProps) => {
  const [text, setText] = useState("");
  const [image, setImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(null);
    setImagePreview(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    if (!file) {
      clearImage();
      return;
    }
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const submit = () => {
    const trimmed = text.trim();
    if (!trimmed && !image) return;
    onSend(trimmed, image);
    setText("");
    clearImage();
  };

  const handleKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 1 }}>
      {imagePreview && (
        <Box
          sx={{
            position: "relative",
            display: "inline-block",
            mb: 1,
          }}
        >
          <Box
            component="img"
            src={imagePreview}
            alt="staged"
            sx={{
              maxHeight: 96,
              maxWidth: "100%",
              borderRadius: 1,
              display: "block",
            }}
          />
          <IconButton
            size="small"
            onClick={clearImage}
            aria-label="Remove attached image"
            sx={{
              position: "absolute",
              top: -8,
              right: -8,
              bgcolor: "background.paper",
              border: "1px solid",
              borderColor: "divider",
              "&:hover": { bgcolor: "background.paper" },
            }}
          >
            <Close fontSize="small" />
          </IconButton>
        </Box>
      )}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1}
        sx={{ alignItems: { xs: "stretch", sm: "flex-end" } }}
      >
        <TextField
          value={text}
          onChange={(event) => setText(event.target.value)}
          onKeyDown={handleKey}
          placeholder="Message Gemma 4… (Shift+Enter for newline)"
          multiline
          minRows={1}
          maxRows={6}
          fullWidth
          disabled={disabled}
          size="small"
        />
        <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFile}
          />
          <Tooltip title="Attach image">
            <span>
              <IconButton
                onClick={() => fileInputRef.current?.click()}
                disabled={disabled}
                aria-label="Attach image"
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <AttachFile />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Send">
            <span>
              <IconButton
                color="primary"
                onClick={submit}
                disabled={disabled || (!text.trim() && !image)}
                aria-label="Send"
                sx={{ minWidth: 44, minHeight: 44 }}
              >
                <Send />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </Paper>
  );
};
